/**
 * Campos compartidos por las fichas en variante tarjetas.
 *
 * Nacen en el Resumen de servidor y se extraen aquí para que todas las fichas
 * pinten igual (FICHA_ESTANDAR_TARJETAS §12):
 *  - en lectura se ve el VALOR, nunca un campo deshabilitado (C7);
 *  - los desplegables de catálogo llevan el `+` de alta en línea pegado al
 *    campo (C13), con ✓ guardar / ✗ cancelar, Enter guarda y Esc cancela;
 *  - si el valor guardado no está en el catálogo se conserva en la lista para
 *    que no desaparezca al abrir el desplegable.
 *
 * Todos reciben `onChange` con el evento nativo: se conectan directamente al
 * `handleChange` del formulario padre.
 */
import React, { useState } from 'react';
import { PlusIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

export function Valor({ label, children, muted = false }) {
  return (
    <div className="sf2-field">
      <label>{label}</label>
      <div className={`v${muted ? ' sf2-muted' : ''}`}>{children || '—'}</div>
    </div>
  );
}

export function CampoTexto({ label, name, value, onChange, editing, placeholder, required = false, type = 'text', action = null }) {
  if (!editing) return <Valor label={label}>{value}</Valor>;
  const input = (
    <input type={type} name={name} value={value || ''} onChange={onChange} placeholder={placeholder} required={required} />
  );
  return (
    <div className="sf2-field">
      <label>{label}</label>
      {action
        ? <div className="sf2-input-row">{input}{action}</div>
        : input}
    </div>
  );
}

export function CampoSelect({
  label,
  name,
  value,
  onChange,
  editing,
  options = [],
  onAdd = null,
  addPlaceholder = 'Nuevo valor',
  addLabel = 'Añadir al catálogo',
  disabled = false,
}) {
  const [abierto, setAbierto] = useState(false);
  const [nuevo, setNuevo] = useState('');
  const actualValue = String(value ?? '');
  const tieneValor = options.some((o) => String(o.value) === actualValue);
  // Si el valor guardado no está en el catálogo (escrito a mano en la ficha
  // clásica) se añade para no perderlo al abrir el desplegable.
  const lista = tieneValor || !actualValue
    ? options
    : [{ value: actualValue, label: actualValue }, ...options];
  const actual = lista.find((o) => String(o.value) === actualValue);
  if (!editing) return <Valor label={label}>{actual?.label || '—'}</Valor>;

  const guardarNuevo = async () => {
    const v = nuevo.trim();
    if (!v || !onAdd) return;
    await onAdd(v);
    setNuevo('');
    setAbierto(false);
  };

  return (
    <div className="sf2-field">
      <label>{label}</label>
      <div className="sf2-input-row">
        <select name={name} value={actualValue} onChange={onChange} disabled={disabled}>
          {lista.map((o) => <option key={String(o.value)} value={String(o.value)}>{o.label}</option>)}
        </select>
        {onAdd && (
          <button
            type="button"
            className="sf2-icon-btn"
            title={addLabel}
            aria-label={addLabel}
            onClick={() => { setAbierto((v) => !v); setNuevo(''); }}
          >
            <PlusIcon style={{ width: 16, height: 16 }} />
          </button>
        )}
      </div>
      {abierto && (
        <div className="sf2-input-row" style={{ marginTop: 8 }}>
          <input
            value={nuevo}
            onChange={(e) => setNuevo(e.target.value)}
            placeholder={addPlaceholder}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); guardarNuevo(); }
              else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setAbierto(false); setNuevo(''); }
            }}
          />
          <button type="button" className="sf2-icon-btn" title="Guardar en el catálogo" aria-label="Guardar en el catálogo" onClick={guardarNuevo}>
            <CheckIcon style={{ width: 16, height: 16 }} />
          </button>
          <button type="button" className="sf2-icon-btn" title="Cancelar" aria-label="Cancelar" onClick={() => { setAbierto(false); setNuevo(''); }}>
            <XMarkIcon style={{ width: 16, height: 16 }} />
          </button>
        </div>
      )}
    </div>
  );
}

/** Opciones de un catálogo de nombres (`[{name}]`) listas para `CampoSelect`. */
export function opcionesDeCatalogo(items = [], etiquetaVacia = '— Seleccionar —') {
  return [
    { value: '', label: etiquetaVacia },
    ...[...new Set(items.map((i) => i.name).filter(Boolean))].map((n) => ({ value: n, label: n })),
  ];
}
